import React from "react";
import moment from "moment";
import {
  Box, IconButton, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Copy01Icon, Delete02Icon, EyeIcon, EyeOffIcon, PencilEdit01Icon} from "@hugeicons/core-free-icons";

const COLUMNS = ["KEY", "OWNER", "ORGANIZATION", "CREATED", "EXPIRES"];

const headCellSx = {
  height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
  bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1.25, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7", whiteSpace: "nowrap"};
const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};
const deleteIconButtonSx = {...iconButtonSx, "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"}};

const formatDate = (date) => date ? moment(date).format("DD MMM YYYY, HH:mm") : "—";

export const ApiKeysTable = ({
  apiKeys, loading, emptyText, revealedKeys, onToggleReveal, onCopy, onEdit, onDelete, size, hideOrganization, readOnly
}) => {
  const columns = hideOrganization ? COLUMNS.filter(column => column !== "ORGANIZATION") : COLUMNS;

  return (
    <TableContainer sx={{width: "100%", overflowX: "auto"}}>
      <Table size={size || "small"} sx={{minWidth: hideOrganization ? 640 : 820}}>
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={column} sx={{...headCellSx, ...(index === 0 && {pl: 2.5})}}>{column}</TableCell>
            ))}
            {!readOnly && <TableCell sx={{...headCellSx, pr: 2.5}}/>}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && Array.from({length: 4}).map((_, index) => (
            <TableRow key={index}>
              <TableCell colSpan={columns.length + (readOnly ? 0 : 1)} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell>
            </TableRow>
          ))}
          {!loading && apiKeys.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length + (readOnly ? 0 : 1)} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                {emptyText}
              </TableCell>
            </TableRow>
          )}
          {!loading && apiKeys.map(apiKey => (
            <ApiKeyRow
              key={apiKey.id}
              apiKey={apiKey}
              revealedKey={revealedKeys[apiKey.id]}
              hideOrganization={hideOrganization}
              readOnly={readOnly}
              onToggleReveal={onToggleReveal}
              onCopy={onCopy}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ApiKeyRow = ({apiKey, revealedKey, hideOrganization, readOnly, onToggleReveal, onCopy, onEdit, onDelete}) => {
  const key = revealedKey || apiKey.maskedKey;

  return (
    <TableRow hover sx={{"&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}}}>
      <TableCell sx={{...cellSx, pl: 2.5}}>
        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
          <Tooltip title={key}>
            <Typography
              noWrap
              sx={{fontFamily: "monospace", fontSize: 12.5, color: "inherit", maxWidth: 240, userSelect: revealedKey ? "all" : "none"}}
            >
              {key}
            </Typography>
          </Tooltip>
          {!readOnly && (
            <>
          <Tooltip title={revealedKey ? "Hide" : "Reveal"}>
            <IconButton size={"small"} sx={iconButtonSx} onClick={() => onToggleReveal(apiKey)}>
              <HugeiconsIcon icon={revealedKey ? EyeOffIcon : EyeIcon} size={16}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Copy"}>
            <IconButton size={"small"} sx={iconButtonSx} onClick={() => onCopy(apiKey)}>
              <HugeiconsIcon icon={Copy01Icon} size={16}/>
            </IconButton>
          </Tooltip>
            </>
          )}
        </Stack>
      </TableCell>
      <TableCell sx={cellSx}>{apiKey.username}</TableCell>
      {!hideOrganization && (
        <TableCell sx={cellSx}>
          <Box component={"span"} sx={{
            px: 1, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 600,
            color: apiKey.organizationName ? "#4B55C9" : "#6B6B80",
            bgcolor: apiKey.organizationName ? "rgba(132, 145, 247, 0.12)" : "#F2F2F7",
          }}>
            {apiKey.organizationName || "Personal"}
          </Box>
        </TableCell>
      )}
      <TableCell sx={{...cellSx, color: "#6B6B80"}}>{formatDate(apiKey.dateCreated)}</TableCell>
      <TableCell sx={{...cellSx, color: "#6B6B80"}}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          {formatDate(apiKey.validUntil)}
          {apiKey.expired && (
            <Box component={"span"} sx={{px: 1, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 600, color: "#DC2626", bgcolor: "rgba(220, 38, 38, 0.08)"}}>
              Expired
            </Box>
          )}
        </Stack>
      </TableCell>
      {!readOnly && (
      <TableCell sx={{...cellSx, pr: 2.5}}>
        <Stack direction={"row"} gap={0.25} justifyContent={"flex-end"}>
          <Tooltip title={"Change expiry"}>
            <IconButton size={"small"} sx={iconButtonSx} onClick={() => onEdit(apiKey)}>
              <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Revoke"}>
            <IconButton size={"small"} sx={deleteIconButtonSx} onClick={() => onDelete(apiKey)}>
              <HugeiconsIcon icon={Delete02Icon} size={16}/>
            </IconButton>
          </Tooltip>
        </Stack>
      </TableCell>
      )}
    </TableRow>
  );
};
